import { type IColumn } from '@fluentui/react';
import { ListCell } from '@h2oai/ui-kit';
import React from 'react';

import { Workspace } from '../../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { RowHeaderTitle } from '../../../components/RowHeaderTitle/RowHeaderTitle';
import { formatDefaultDateAndTime, formatRelativeTime } from '../../../utils/utils';
import { WorkspaceItemActions } from '../components/Parts/Parts';
import { Owner, SharedWith } from '../components/WorkspaceCells/WorkspaceCells';

interface UseColumnsParams {
  setWorkspaceForEdit: (w: Workspace) => void;
  setWorkspaceForView: (w: Workspace) => void;
}

export const useColumns = ({ setWorkspaceForEdit, setWorkspaceForView }: UseColumnsParams): IColumn[] => {
  return React.useMemo<IColumn[]>(
    () => [
      {
        key: 'title',
        name: 'Title',
        fieldName: 'name',
        minWidth: 420,
        maxWidth: 500,
        data: {
          headerFieldName: 'displayName',
          listCellProps: {
            onRenderHeader: (workspace: Workspace) => RowHeaderTitle({ title: workspace.displayName }),
            iconProps: {
              iconName: 'Stack',
            },
          },
        },
      },
      {
        key: 'owner',
        name: 'Owner',
        fieldName: 'owner',
        minWidth: 180,
        maxWidth: 250,
        onRender: (workspace: Workspace) => {
          return <Owner workspaceName={workspace.name!} />;
        },
      },
      {
        key: 'sharedWith',
        name: 'Shared with',
        fieldName: '',
        minWidth: 180,
        maxWidth: 200,
        data: {
          listCellProps: {
            emptyMessage: 'Users',
          },
        },
        onRender: (workspace: Workspace) => {
          return <SharedWith workspaceName={workspace.name!} />;
        },
      },
      {
        key: 'description',
        fieldName: 'description',
        name: 'Description',
        minWidth: 200,
        isCollapsible: true,
        data: {
          listCellProps: {
            emptyMessage: <span>&nbsp;</span>,
          },
        },
      },
      {
        key: 'createTime',
        minWidth: 180,
        name: 'Created',
        data: {
          listCellProps: {
            emptyMessage: 'Unknown',
          },
        },
        onRender: (workspace: Workspace) => {
          const isDeleted = Boolean(workspace.deleteTime);
          const formattedDate = formatDefaultDateAndTime(isDeleted ? workspace.deleteTime : workspace.createTime);
          const label = isDeleted ? 'Deleted on' : 'Created';

          return (
            <ListCell
              headerText={label}
              text={formattedDate}
              onRenderText={() =>
                formattedDate ? <time dateTime={formattedDate}>{formattedDate}</time> : <>Not changed yet</>
              }
            />
          );
        },
      },
      {
        key: 'updateTime',
        minWidth: 180,
        name: 'Last updated',
        data: {
          listCellProps: {
            emptyMessage: 'Unknown',
          },
        },
        onRender: (workspace: Workspace) => {
          const isDeleted = Boolean(workspace.deleteTime);
          const formattedDate = isDeleted
            ? formatRelativeTime(workspace.purgeTime)
            : formatDefaultDateAndTime(isDeleted ? workspace.deleteTime : workspace.updateTime);
          const label = isDeleted ? 'Purge in' : 'Last updated';

          return (
            <ListCell
              headerText={label}
              text={formattedDate}
              onRenderText={() =>
                formattedDate ? <time dateTime={formattedDate}>{formattedDate}</time> : <>Not changed yet</>
              }
            />
          );
        },
      },
      {
        key: 'actions',
        name: 'Actions',
        fieldName: '',
        minWidth: 200,

        styles: {
          root: {
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'end',
            ':has(.workspace-active)': {
              // TODO: Use theme color.
              backgroundImage: 'linear-gradient(to right, white , var(--h2o-green100, #D0F5D1))',
              borderRadius: 2,
            },
          },
        },
        data: {
          listCellProps: {
            styles: {
              root: {
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'end',
                ':has(.workspace-active)': {
                  // TODO: Use theme color.
                  backgroundImage: 'linear-gradient(to right, white , var(--h2o-green100, #D0F5D1))',
                  borderRadius: 2,
                },
              },
            },
          },
        },
        onRender: (workspace: Workspace) => (
          <WorkspaceItemActions
            workspace={workspace}
            setWorkspaceForEdit={() => {
              setWorkspaceForEdit(workspace);
            }}
            setWorkspaceForView={() => {
              setWorkspaceForView(workspace);
            }}
          />
        ),
      },
    ],
    [setWorkspaceForEdit, setWorkspaceForView]
  );
};
